// SPDX-FileCopyrightText: Copyright Orangebot, Inc.
// SPDX-License-Identifier: Apache-2.0

/**
 * Helpers for cleaning up call signaling Communications when the browser
 * is leaving the page. React lifecycle cleanup (`useEffect` return) is not
 * reliable for tab close, browser quit, or network drop — by the time React
 * runs cleanup, the page is already being destroyed and any async HTTP
 * request is cancelled.
 *
 * Use `fetch({ keepalive: true })` instead. The browser commits the request
 * to a separate pool that outlives the page. PATCH with a json-patch body
 * lets us update Communication.status + Communication.statusReason
 * atomically.
 */

import type { MedplumClient } from '@medplum/core';
import { CARECOMM_OUTCOME, carecommOutcomeReason, type CareCommOutcomeCode } from './organizationSettings';

interface UnloadPatchOptions {
  medplum: MedplumClient;
  communicationId: string;
  outcome: CareCommOutcomeCode;
}

/**
 * Patch a Communication to status=completed + statusReason in a way that
 * survives `pagehide`/`unload`. Returns synchronously; the browser keeps
 * the request alive until the response (or timeout) — do NOT await this on
 * an unload path because awaiting can be cancelled when the page goes away.
 */
export function patchCommunicationOnUnload(opts: UnloadPatchOptions): void {
  const { medplum, communicationId, outcome } = opts;
  if (!communicationId) return;

  const accessToken = medplum.getAccessToken();
  if (!accessToken) return;

  const baseUrl = medplum.getBaseUrl();
  const url = `${baseUrl}fhir/R4/Communication/${communicationId}`;
  const body = JSON.stringify([
    { op: 'replace', path: '/status', value: 'completed' },
    { op: 'add', path: '/statusReason', value: carecommOutcomeReason(outcome) },
  ]);

  try {
    fetch(url, {
      method: 'PATCH',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json-patch+json',
        Authorization: `Bearer ${accessToken}`,
      },
      body,
    }).catch(() => {
      // best-effort; nothing useful we can do during unload
    });
  } catch {
    // ignore
  }
}

/**
 * Patch via the normal awaited path. Use for in-app event handlers
 * (Agora disconnect, normal hangup) where we have time to await.
 */
export async function patchCommunicationOutcome(
  medplum: MedplumClient,
  communicationId: string,
  outcome: CareCommOutcomeCode
): Promise<void> {
  if (!communicationId) return;
  await medplum.patchResource('Communication', communicationId, [
    { op: 'replace', path: '/status', value: 'completed' },
    { op: 'add', path: '/statusReason', value: carecommOutcomeReason(outcome) },
  ]);
}

export const CARECOMM_OUTCOMES = CARECOMM_OUTCOME;
